import { memo, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useTheme } from '@mui/material'
import { DomainOutlined, LockOutlined, MoreVertOutlined, PublicOutlined } from '@mui/icons-material'
import { ContextMenuItem, ContextMenuIcon } from 'features/common/ui/ContextMenu'
import CardBasicInformation from 'features/common/ui/Cards/CardBasicInformation'
import useUserRoles from 'features/account/state/useUserRoles'
import { WorkflowDemo, WorkflowListItem } from 'features/workflows/types'
import { useDuplicateWorkflow } from 'features/workflows/hooks/useDuplicateWorkflow'
import { useOpenDemoWorkflow } from 'features/workflows/hooks/demoWorkflows'
import { showDeleteConfirmationDialog, showWorkflowEditNameDialog, showWorkflowShareDialog } from '../Dialogs'
import { CommonPrivacy } from 'features/common'
import { useAppSelector } from 'app/state'
import { workflowSelectors } from 'features/workflows/state/workflowSelectors'

enum WorkflowActions {
  Edit = 'edit',
  Share = 'share',
  Duplicate = 'duplicate',
  Delete = 'delete',
  CreateFromTemplate = 'createFromTemplate'
}

function WorkflowCardHeader({ workflow, demo }: { workflow: WorkflowListItem; demo?: WorkflowDemo }) {
  const intl = useIntl()
  const theme = useTheme()
  const { userIsViewer, userHasEditionRole } = useUserRoles()
  const { duplicate, isLoading: isDuplicating } = useDuplicateWorkflow(workflow)
  const isEditorMode = useAppSelector(workflowSelectors.isEditorMode)
  const isDemo = Boolean(demo) ?? false
  const exists = demo?.exists ?? false
  const me = useAppSelector((state) => state.users.me)
  const isOwner = workflow?.userId === me?.user_id

  const openDemoWorkflow = useOpenDemoWorkflow()

  const actions = useMemo(
    () => [
      {
        id: WorkflowActions.CreateFromTemplate,
        label: intl.formatMessage({ id: 'workflows.cards.actions.editThisDemo' }),
        hidden: !isDemo || exists
      },
      {
        id: WorkflowActions.Edit,
        label: intl.formatMessage({ id: 'workflows.cards.actions.edit' }),
        hidden: !isEditorMode
      },
      {
        id: WorkflowActions.Share,
        label: intl.formatMessage({ id: 'workflows.cards.actions.share' }),
        hidden: !isEditorMode,
        disabled: !workflow.title,
        tooltipId: !workflow.title ? 'workflows.shareDisabledTooltip' : undefined
      },
      {
        id: WorkflowActions.Duplicate,
        label: intl.formatMessage({ id: 'workflows.cards.actions.duplicate' }),
        hidden: (isDemo && !exists) || !userHasEditionRole,
        disabled: isDuplicating
      },
      {
        id: WorkflowActions.Delete,
        label: intl.formatMessage({ id: 'workflows.cards.actions.delete' }),
        destructive: true,
        hidden: !isEditorMode || !isOwner
      }
    ],
    [exists, intl, isDemo, isDuplicating, isEditorMode, isOwner, userHasEditionRole, workflow.title]
  )

  const handleClickOption = (item: ContextMenuItem) => {
    if (item.id === WorkflowActions.Edit) {
      showWorkflowEditNameDialog({ workflow })
    }
    if (item.id === WorkflowActions.Delete) {
      showDeleteConfirmationDialog({ workflow })
    }
    if (item.id === WorkflowActions.Duplicate) {
      duplicate()
    }
    if (item.id === WorkflowActions.Share) {
      showWorkflowShareDialog({ workflow })
    }
    if (item.id === WorkflowActions.CreateFromTemplate) {
      demo && openDemoWorkflow(demo)
    }
  }

  let miniIconProps

  // TODO: Public workflows are in an early stage. The backend
  // does not return privacy: "public", but instead public: true.
  // When the backend starts using privacy for the public workflows,
  // this code needs to be updated to use CommonPrivacy.Public instead.
  if (workflow.public) {
    miniIconProps = {
      icon: <PublicOutlined aria-label="Public workflow" />,
      tooltip: true,
      tooltipLabelId: 'workflows.cards.tooltips.public',
      type: 'public'
    }
  } else if (workflow.privacy === CommonPrivacy.Shared) {
    miniIconProps = {
      icon: <DomainOutlined aria-label="Shared with organization" />,
      tooltip: true,
      tooltipLabelId: 'workflows.cards.tooltips.shared',
      type: 'shared'
    }
  } else if (workflow.privacy === CommonPrivacy.Private) {
    miniIconProps = {
      icon: <LockOutlined aria-label="Private workflow" />,
      tooltip: true,
      tooltipLabelId: 'workflows.cards.tooltips.private',
      type: 'private'
    }
  }

  return (
    <>
      <CardBasicInformation
        minHeight={theme.spacing(10)}
        title={workflow.title || intl.formatMessage({ id: 'workflows.default.title' })}
        miniIconProps={miniIconProps}
        action={
          !userIsViewer ? (
            <ContextMenuIcon
              placement="bottom-end"
              aria-label="workflow-actions-menu"
              onClickItem={handleClickOption}
              menuItems={actions}
            >
              <MoreVertOutlined fill={theme.palette.text.secondary} />
            </ContextMenuIcon>
          ) : null
        }
      />
    </>
  )
}

export default memo(WorkflowCardHeader)
